import { useEffect, useState } from "react"
import useEmblaCarousel from "embla-carousel-react"

import { Lottie } from "@/components/ui/lottie"
import { cn } from "@/lib/utils"

import ChevronRight from "@/assets/svg/compatibility/chevron-right.svg?react"
import loaderCircle from "@/assets/json/Loader_Circle_16x16_purple_transp_40.json"

import {
  REPORT_PROMO_SLIDE_IDS,
  REPORT_PROMO_SUBTITLES,
  type ReportPromoId,
} from "../../config/report-promo-banners"
import { getReportById, type ReportItem } from "../../config/reports-data"
import { useReportPurchaseStore } from "../../hooks/report-purchase-store"
import { useReportCta } from "../../hooks/use-report-cta"
import { useReportStates } from "../../hooks/use-report-states"
import { useReportUnlock } from "../../hooks/use-report-unlock"

type PromoReport = ReportItem & { id: ReportPromoId }

const SLIDES = REPORT_PROMO_SLIDE_IDS.map((id) => getReportById(id)).filter(
  (r): r is PromoReport => !!r,
)

interface ReportPromoSliderProps {
  userMainSubscriptionId: string | null
  className?: string
}

/**
 * Readings promo carousel — one slide per purchasable report.
 * States: Get for $X · Preparing · View report (Figma: 573:9421).
 */
export function ReportPromoSlider({
  userMainSubscriptionId,
  className,
}: ReportPromoSliderProps) {
  const { handleReportCta, isReportPurchasing } = useReportCta({ userMainSubscriptionId })
  const stateOf = useReportStates()

  const [emblaRef, emblaApi] = useEmblaCarousel({ align: "start", loop: false })
  const [selectedIndex, setSelectedIndex] = useState(0)

  useEffect(() => {
    if (!emblaApi) return
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap())
    onSelect()
    emblaApi.on("select", onSelect)
    return () => {
      emblaApi.off("select", onSelect)
    }
  }, [emblaApi])

  return (
    <div className={cn("flex flex-col gap-2", className)} data-slot="report-promo-slider">
      {/* Bleed past shell px-4 so slides clip at the screen edge, not the content inset. */}
      <div className="-mx-4">
        <div ref={emblaRef} className="overflow-hidden">
          <div className="flex">
            {SLIDES.map((report) => {
              const { purchased, price } = stateOf(report)
              return (
                <div key={report.id} className="min-w-0 flex-[0_0_100%] px-4">
                  <ReportPromoBanner
                    report={report}
                    purchased={purchased}
                    price={price}
                    isPurchasing={isReportPurchasing(report)}
                    onCta={() => handleReportCta(report, purchased)}
                  />
                </div>
              )
            })}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center gap-3 py-1">
        {SLIDES.map((report, i) => (
          <button
            key={report.id}
            type="button"
            aria-label={`Go to ${report.title}`}
            onClick={() => emblaApi?.scrollTo(i)}
            className={cn(
              "size-1.5 rounded-full transition-colors",
              i === selectedIndex ? "bg-purple-light" : "bg-purple-light/30",
            )}
          />
        ))}
      </div>
    </div>
  )
}

function ReportPromoBanner({
  report,
  purchased,
  price,
  isPurchasing,
  onCta,
}: {
  report: PromoReport
  purchased: boolean
  price: number
  isPurchasing: boolean
  onCta: () => void
}) {
  const { inProgress, remainingLabel } = useReportUnlock(report.id)
  const showSheet = useReportPurchaseStore((s) => s.showSheet)
  const subtitle = REPORT_PROMO_SUBTITLES[report.id]

  const handleClick = () => {
    if (inProgress) {
      showSheet(report)
      return
    }
    onCta()
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isPurchasing && !inProgress}
      className="flex w-full items-center overflow-hidden rounded-[20px] bg-dark-surface pl-4 text-left disabled:opacity-80"
    >
      <span className="flex min-w-0 flex-1 flex-col gap-2 py-4">
        <span className="flex flex-col gap-1">
          <span className="font-display text-base font-medium text-purple-light">
            {report.title}
          </span>
          <span className="font-body text-sm text-purple-light/70">{subtitle}</span>
        </span>

        {inProgress ? (
          <span className="inline-flex h-[29px] w-fit items-center gap-1 rounded-[15px] bg-purple-light/10 py-2 pl-2 pr-3">
            <Lottie animationData={loaderCircle} className="size-4 shrink-0" />
            <span className="font-body text-[10px] font-medium uppercase text-purple-light/70">
              Preparing · {remainingLabel}
            </span>
          </span>
        ) : purchased ? (
          <span className="inline-flex h-[29px] w-fit items-center rounded-[15px] bg-purple-light/10 py-2 pl-3 pr-1">
            <span className="font-body text-[10px] font-medium uppercase text-purple-light">
              View report
            </span>
            <span className="flex size-4 items-center justify-center text-purple-light [&_svg]:w-[5px]">
              <ChevronRight />
            </span>
          </span>
        ) : (
          <span className="inline-flex w-fit items-center rounded-[15px] bg-green/20 px-3 py-2">
            <span className="font-body text-[10px] font-medium uppercase text-green">
              Get for ${price}
            </span>
          </span>
        )}
      </span>

      <img
        src={report.sliderCover ?? report.cover}
        alt=""
        className="h-[133px] w-[120px] shrink-0 object-cover object-top"
      />
    </button>
  )
}
